'use client';

import { OptimizedImage } from '@/components/OptimizedImage/OptimizedImage';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import styles from './SiteLayout.module.css';
import { isGoogleAdsConversionLink, trackGoogleAdsConversion } from '@/utils/gtag';
import { SUPPORT_PHONE_READABLE, SUPPORT_PHONE_E164, SALES_PHONE_READABLE, SALES_PHONE_E164, buildWhatsAppUrl } from '@/utils/whatsapp';
import { IconPhone, IconWhatsApp, IconCart, IconMenu, IconClose } from '@/components/Icons/Icons';

export function SiteLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Ensure route navigations start at top
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Close mobile menu on route change
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    // Lock body scroll when mobile menu is open
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleConversionClick = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest('a');
      if (!link) return;

      const href = link.getAttribute('href') ?? '';
      if (isGoogleAdsConversionLink(href)) {
        trackGoogleAdsConversion();
      }
    };

    document.addEventListener('click', handleConversionClick);
    return () => document.removeEventListener('click', handleConversionClick);
  }, []);

  useEffect(() => {
    // Header scroll animation - Performance optimized
    let rafId: number | null = null;
    let lastScrollY = 0;

    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        const scrolled = window.scrollY;
        
        // Throttle: Only update if scroll changed significantly
        if (Math.abs(scrolled - lastScrollY) < 5) {
          rafId = null;
          return;
        }
        
        lastScrollY = scrolled;
        setIsScrolled(scrolled > 20);
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className={styles.shell}>
      <a className="skipLink" href="#main">
        İçeriğe atla
      </a>

      <header className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}>
        {/* Mobil üst bar - Müşteri Hizmetleri */}
        <div className={styles.mobileTopBar}>
          <div className="container">
            <a className={styles.mobilePhone} href={`tel:${SUPPORT_PHONE_E164}`}>
              <IconPhone /> <span>Müşteri Hizmetleri: {SUPPORT_PHONE_READABLE}</span>
            </a>
          </div>
        </div>

        <div className={`container ${styles.headerInner}`}>
          <Link className={styles.brand} to="/" aria-label="Echopan Anasayfa">
            <OptimizedImage
              className={styles.logo}
              src="/images/header-footer-logo.webp"
              alt="Echopan"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </Link>

          <nav className={styles.nav} aria-label="Ana menü">
            <NavLink to="/" end>
              Anasayfa
            </NavLink>
            <div className={styles.dropdownWrapper}>
              <NavLink to="/urunler" className={styles.dropdownTrigger}>
                Ürünler <span className={styles.dropdownArrow}>▾</span>
              </NavLink>
              <div className={styles.dropdownMenu}>
                <Link to="/bondex-sunger" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/bondex.webp" alt="Bondex Sünger" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Bondex Sünger</strong>
                    <span>Yüksek yoğunluklu rebonded süngerpan ses yalıtımı çözümü.</span>
                  </div>
                </Link>
                <Link to="/bariyerli-bondex-sunger" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/bariyerli.webp" alt="Bariyerli Bondex Sünger" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Bariyerli Bondex Sünger</strong>
                    <span>Bariyer katmanlı ses yalıtım süngeri.</span>
                  </div>
                </Link>
                <Link to="/cift-bariyerli-bondex-sunger" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/cift-bariyer.webp" alt="Çift Bariyerli Bondex Sünger" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Çift Bariyerli Bondex Sünger</strong>
                    <span>Çift bariyer katmanlı premium çözüm.</span>
                  </div>
                </Link>
                <Link to="/bariyerli-bondex-sunger-23mm" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/bariyerli.webp" alt="Bariyerli Bondex Sünger 23mm" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Bariyerli Bondex Sünger 23mm</strong>
                    <span>İnce kompozit çözüm, pratik uygulama.</span>
                  </div>
                </Link>
                <Link to="/bariyerli-bondex-sunger-33mm" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/bariyerli.webp" alt="Bariyerli Bondex Sünger 33mm" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Bariyerli Bondex Sünger 33mm</strong>
                    <span>Performans/kalınlık dengesi için güçlü seçim.</span>
                  </div>
                </Link>
                <Link to="/bariyerli-bondex-sunger-43mm" className={styles.dropdownItem}>
                  <OptimizedImage src="/images/bariyerli.webp" alt="Bariyerli Bondex Sünger 43mm" className={styles.dropdownImg} width={72} height={72} />
                  <div className={styles.dropdownItemText}>
                    <strong>Bariyerli Bondex Sünger 43mm</strong>
                    <span>Yüksek izolasyon ihtiyacı için ideal.</span>
                  </div>
                </Link>
              </div>
            </div>
            <NavLink to="/blog">Blog</NavLink>
            <NavLink className={styles.navProductLink} to="/bondex-sunger">Bondex Sünger</NavLink>
            <NavLink className={styles.navProductLink} to="/bariyerli-bondex-sunger">Bariyerli Bondex Sünger</NavLink>
            <NavLink className={styles.navProductLink} to="/cift-bariyerli-bondex-sunger">Çift Bariyerli Bondex</NavLink>
            <NavLink to="/sepet">
              <IconCart /> Sepet
            </NavLink>
            <NavLink to="/iletisim">İletişim</NavLink>
          </nav>

          <div className={styles.headerRight} aria-label="İletişim ve hızlı işlem">
            <a className={styles.headerPhone} href={`tel:${SUPPORT_PHONE_E164}`}>
              <IconPhone /> {SUPPORT_PHONE_READABLE}
            </a>
            <a className={styles.headerEmail} href={`tel:${SALES_PHONE_E164}`}>
              <IconWhatsApp /> {SALES_PHONE_READABLE}
            </a>
            <a className={styles.headerCta} href={buildWhatsAppUrl('Merhaba, kurumsal teklif almak istiyorum.')}>
              Teklif Al
            </a>
          </div>

          <button
            className={styles.mobileMenuButton}
            onClick={toggleMobileMenu}
            aria-label="Menüyü aç/kapat"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <IconClose /> : <IconMenu />}
          </button>
        </div>

        {/* Mobil Menü */}
        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
          <nav className={styles.mobileNav} aria-label="Mobil ana menü">
            <NavLink to="/" end onClick={toggleMobileMenu}>
              Anasayfa
            </NavLink>
            <NavLink to="/urunler" onClick={toggleMobileMenu}>Ürünler</NavLink>
            <NavLink to="/blog" onClick={toggleMobileMenu}>Blog</NavLink>
            <NavLink to="/bondex-sunger" onClick={toggleMobileMenu}>Bondex Sünger</NavLink>
            <NavLink to="/bariyerli-bondex-sunger" onClick={toggleMobileMenu}>Bariyerli Bondex Sünger</NavLink>
            <NavLink to="/cift-bariyerli-bondex-sunger" onClick={toggleMobileMenu}>Çift Bariyerli Bondex</NavLink>
            <NavLink to="/sepet" onClick={toggleMobileMenu}>
              <IconCart /> Sepet
            </NavLink>
            <NavLink to="/iletisim" onClick={toggleMobileMenu}>İletişim</NavLink>
            <div className={styles.mobileContactBlock}>
              <a className={styles.mobileContactLink} href={`tel:${SUPPORT_PHONE_E164}`}>
                <IconPhone /> {SUPPORT_PHONE_READABLE}
              </a>
              <a className={styles.mobileContactLink} href={`tel:${SALES_PHONE_E164}`}>
                <IconWhatsApp /> {SALES_PHONE_READABLE}
              </a>
            </div>
          </nav>
          <div className={styles.mobileMenuActions}>
            <a className={styles.mobileMenuCta} href={buildWhatsAppUrl('Merhaba, kurumsal teklif almak istiyorum.')}>
              Teklif Al
            </a>
          </div>
        </div>
      </header>

      <main id="main" className={styles.main}>
        {children}
      </main>

      <footer className={styles.footer}>
        <div className={`container ${styles.footerWrap}`}>
          <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <Link className={styles.footerBrandTop} to="/" aria-label="Bondex Sünger">
              <OptimizedImage className={styles.footerLogo} src="/images/header-footer-logo.webp" alt="Echopan" loading="lazy" />
            </Link>
            <div className={styles.footerBrandText}>
              <strong>Bondex Sünger</strong>
              <span>Kurumsal tedarik • Türkiye geneli</span>
            </div>
            <p className={styles.footerText}>
              bariyerlibondexsunger.com üzerinden Bondex sünger, Bariyerli Bondex Sünger ve Çift Bariyerli Bondex ürünlerini proje
              ihtiyacınıza uygun şekilde seçmenize yardımcı oluyoruz.
            </p>
            <p className={styles.footerSeoCredit}>
              <a href="https://www.spindorai.com/seo/seo-hizmeti" target="_blank" rel="dofollow" className={styles.footerSeoLink}>
                Seo Hizmeti
              </a>{' '}
              Spindora
            </p>
          </div>

          <div>
            <h3 className={styles.footerTitle}>Ürünler</h3>
            <div className={styles.footerLinks}>
              <Link to="/bondex-sunger">Bondex Sünger</Link>
              <Link to="/bariyerli-bondex-sunger">Bariyerli Bondex Sünger</Link>
              <Link to="/cift-bariyerli-bondex-sunger">Çift Bariyerli Bondex</Link>
              <Link to="/urunler">Tüm ürünler</Link>
            </div>
          </div>

          <div>
            <h3 className={styles.footerTitle}>İletişim</h3>
            <div className={styles.footerList}>
              <a className={styles.footerItem} href={`tel:${SUPPORT_PHONE_E164}`}>
                <IconPhone /> <span>Müşteri Hizmetleri: {SUPPORT_PHONE_READABLE}</span>
              </a>
              <a className={styles.footerItem} href={`tel:${SALES_PHONE_E164}`}>
                <IconPhone /> <span>Satış / Arama: {SALES_PHONE_READABLE}</span>
              </a>
              <a className={styles.footerItem} href={buildWhatsAppUrl('Merhaba, ürünler hakkında bilgi almak istiyorum.')}>
                <IconWhatsApp /> <span>WhatsApp’tan yaz</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className={styles.footerTitle}>Ortak Çalıştığımız Firmalar</h3>
            <div className={`${styles.footerLinks} ${styles.footerLinksPlain}`}>
              <a href="https://tasarimakustik.com" rel="dofollow">
                Tasarım Akustik
              </a>
              <a href="https://akustikpaneller.com/urunler/akustik-panel" rel="dofollow">
                Ses Yalıtımlı Panel
              </a>
              <a href="https://akustikpanel.tr/akustik-panel/" rel="dofollow">
                Akustik Panel
              </a>
              <a href="https://akustikkabinfiyatlari.tr/urunler" rel="dofollow">
                Akustik Kabin
              </a>
              <a href="https://toplantikabini.com" rel="dofollow">
                Toplantı Kabini
              </a>
              <a href="https://toplantikabini.com.tr/akustik-telefon-kabini" rel="dofollow">
                Telefon Kabini
              </a>
            </div>
          </div>

          <div>
            <h3 className={styles.footerTitle}>Bilgiler</h3>
            <div className={styles.footerLinks}>
              <span className={styles.footerInfo}>Güvenli iletişim</span>
              <span className={styles.footerInfo}>Hızlı teklif</span>
              <span className={styles.footerInfo}>Kurumsal destek</span>
              <a
                href="https://aryateknoloji.com/"
                rel="dofollow"
                target="_blank"
                className={styles.footerAgencyLink}
              >
                Web Tasarım Ajansı
              </a>
            </div>
          </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <div className={`container ${styles.footerBottomInner}`}>
            <span className={styles.footerBottomText}>Copyright © {new Date().getFullYear()} Echopan - Bondex Sünger & Bariyerli Bondex Üretici Firma | Tüm Hakları Saklıdır.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
